import React, { useState } from 'react';
import { VictoryAxis, VictoryBar, VictoryChart } from 'victory';

import { DividendPayerData, HighGrowthCompanyData } from '../../../lib/models/fundamental/types';
import { roundPercentValues } from '../../../lib/util/DataHelpers';

import { DEFAULT_FONT_FAMILY } from '../../../constants';
import { useIsMobile } from '../../../hooks';
import { CHART_HEIGHT, CHART_WIDTH, DATUM_SELECTOR, xAxisStyle } from '../DividendPyersAndHighGrowthCompanies.constants';

import styles from './Chart.module.scss';

type Props = {
  data: HighGrowthCompanyData[] | DividendPayerData[];
  yAxis: string;
  currentItemId: string;
  isHighGrowthCompany: boolean
  onItemChange: (id: number) => void
}

const Chart = ({ data, yAxis, isHighGrowthCompany, onItemChange, currentItemId }: Props) => {
  const isMobile = useIsMobile();
  const [selectedSymbol, setSelectedSymbol] = useState(currentItemId);

  if (!data.length) {
    // Heights for the candles in the skeleton loader
    const heights = [140, 120, 110, 100, 80, 60, 50, 40, 30, 20];
    const maxCandleCount = isMobile ? 6 : heights.length;

    return (
      <div className={styles.loader}>
        {heights.slice(0, maxCandleCount).map((height) => (
          <div key={height} className={styles.candle} style={{ height }} />
        ))}
      </div>
    );
  }

  return (
    <svg
      height={CHART_HEIGHT}
      width="100%"
      preserveAspectRatio="none"
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
    >
      <VictoryChart
        standalone={false}
        width={CHART_WIDTH}
        height={CHART_HEIGHT}
        padding={{
          top: 23,
          bottom: 28,
          right: 20,
          left: 20,
        }}
      >
        <VictoryAxis
          style={xAxisStyle}
        />
        <VictoryBar
          data={data}
          x="symbol"
          y={yAxis}
          alignment="middle"
          barWidth={isMobile ? 40 : 18}
          cornerRadius={isMobile ? 8 : 4}
          animate={{
            duration: 500,
            onLoad: { duration: 1000 },
          }}
          labels={({ datum }) => `${
            isHighGrowthCompany ? roundPercentValues(datum.growthIndicator) : Math.round(datum.dividendYield * 100)
          }%`}
          style={{
            labels: { fontSize: 12, fill: '#FFFFFF', fontFamily: DEFAULT_FONT_FAMILY },
            data: {
              cursor: 'pointer',
              stroke: '#3BB0FF26',
              strokeWidth: 1,
              fill: ({ datum }) => (selectedSymbol === datum.symbol ? '#3BB0FF' : '#3BB0FF1A'),
            },
          }}
          events={[{
            target: 'data',
            eventHandlers: {
              onClick: () => [{
                target: 'data',
                mutation: (props: any) => {
                  if (props[DATUM_SELECTOR]) {
                    const { symbol } = props[DATUM_SELECTOR];
                    onItemChange(symbol);
                    setSelectedSymbol(symbol);
                  }
                },
              }],
            },
          }]}
        />
      </VictoryChart>
    </svg>
  );
};

export default Chart;
